import Flickity from 'flickity'
import Vue from 'vue'
import { mapActions } from 'vuex'

import {
  decode,
  formatPrice,
  generateSrcset,
  slug
} from 'utils/general'

/**
 * This component is used throughout the site,
 * in product carousels and on the PLP page.
 */
Vue.component('product-card', resolve => {
  Promise.all([
    enqueueModule('vue-image.vue')
  ]).then(() => resolve({
    props: [
      'product',
      'decode',
      'featured'
    ],
    /**
     * Data can come via a JSON string that needs to be
     * decoded, or via an javascript object, passed by a
     * parent component or a Vuex store.
     */
    data () {
      let _product
      if (!!this.decode && this.product) {
        _product = JSON.parse(decode(this.product))
      } else {
        _product = (this.product || {
          title: '',
          featuredImage: '',
          variants: [],
          optionNames: [],
          optionsWithValues: {},
          swatchImages: '',
          galleryimages: []
        })
      }
      // _product = cleanseProductJson(_product)
      return {
        ..._product,
        selectedColor: false,
        activeVariant: {},
        hasBadges: false,
        productBadges: '',
        productBadgesValues: '',
        imagesLoaded: 0,
        totalImages: 0,
        flkty: null,
        hoverClass: '',
        delayedGalleryImages: [],
        galleryimages: []
      }
    },
    mounted () {
      this.activeVariant = this.variants[0] || {}
      this.title = this.title.replaceAll('[escaped]', '"')

      if (this.swatches) {
        const colorOptionNumber = `option${this.colorOptionIndex + 1}`
        this.selectedColor = slug(this.activeVariant[colorOptionNumber])
      }

      const swatchThumbnails = this.images.filter(image => {
        let altText = ''

        if (image.alt) {
          altText = image.alt.toLowerCase()
        }

        if (altText.includes('swatch_')) {
          return image
        }
      })

      if (swatchThumbnails.length > 0) {
        this.swatchImages = swatchThumbnails.map(swatchThumbnail => ({
          handle: swatchThumbnail.alt.toLowerCase().split('swatch_')[1].split('|')[0].trim().replaceAll(' ', '-'),
          src: swatchThumbnail.src
        }))
      }
      this.checkProductBadges()
      this.checkProductBadgesAllValue()

      if (typeof _swat !== 'undefined') {
        _swat.initializeActionButtons('.collection')
        _swat.initializeActionButtons('.post__product')
      }

      this.filteredImages()
      this.delayedGalleryImages = this.galleryimages.slice(1)
      this.setImageDimensionSlider()

      if (window.innerWidth <= 768) {
        this.$nextTick(() => {
          this.initializeFlickity()
        })
      }
    },
    beforeDestroy () {
      // Destroy Flickity instance on component destroy
      if (this.flkty) {
        this.flkty.destroy()
      }
    },
    computed: {
      quickViewHandle () {
        return `quickView${this.id}`
      },
      productVendor () {
        return this.vendor
      },

      displayVendor () {
        return this.title.split('- ')[0].trim()
      },
      displayTitle () {
        return this.title.split('- ').slice(1).join('- ').trim()
      },
      formattedPrice () {
        let priceHtml = formatPrice(this.activeVariant.price)
        if (this.activeVariant.compareAtPrice !== 'NaN') {
          const discountedPrice = formatPrice(this.activeVariant.price)
          const delPrice = formatPrice(this.activeVariant.compareAtPrice)

          priceHtml = `<del>${delPrice}</del><ins>${discountedPrice}</ins>`
        }
        return priceHtml
      },
      variantsAvailable () {
        let totalVariants = ''

        if (this.variants.length > 1) {
          totalVariants = `+ ${this.variants.length} variants available`
        }
        return totalVariants
      },
      variantsList () {
        let priceHtml = formatPrice(this.activeVariant.price)
        let maxVariant = null
        let minVariant = null

        if (this.activeVariant.compareAtPrice !== 'NaN') {
          const discountedPrice = formatPrice(this.activeVariant.price)
          const delPrice = formatPrice(this.activeVariant.compareAtPrice)

          priceHtml = `<del>${delPrice}</del><ins>${discountedPrice}</ins>`

          if (!this.activeVariant.compareAtPrice) {
            priceHtml = `${discountedPrice}`
          }
        }

        if (this.variants.length) {
          // Get max variant price
          maxVariant = this.variants.reduce(function (prev, current) {
            return (parseInt(prev.price) > parseInt(current.price)) ? prev : current
          })
          // Get min variant price
          minVariant = this.variants.reduce(function (prev, current) {
            return (parseInt(prev.price) < parseInt(current.price)) ? prev : current
          })
        }

        function hasPrice (price) {
          if (price !== 'NaN' && price) {
            return formatPrice(price)
          } else {
            return false
          }
        }

        // const mainPrice = hasPrice(this.activeVariant.price)
        // const discountedMainPrice = hasPrice(this.activeVariant.compareAtPrice)
        const maxVariantPrice = hasPrice(maxVariant.price)
        const minVariantPrice = hasPrice(minVariant.price)
        const discountedMaxVariantPrice = hasPrice(maxVariant.compareAtPrice)
        const discountedMinVariantPrice = hasPrice(minVariant.compareAtPrice)
        let output = ''

        if (minVariantPrice === maxVariantPrice || maxVariantPrice === '') {
          output = priceHtml
        } else {
          if (discountedMaxVariantPrice) {
            output = `<del>${discountedMinVariantPrice} — ${discountedMaxVariantPrice}</del><ins>${minVariantPrice} — ${maxVariantPrice}</ins>`
          } else {
            output = `${minVariantPrice} — ${maxVariantPrice}`
          }
        }

        return output
      },
      getProduct () {
        const product = this
        return product
      },
      promoBadge () {
        let promoBadgeText = ''

        for (const tag of this.tags) {
          const tagLower = tag.toLowerCase()
          if (tagLower.includes('promo:')) {
            promoBadgeText += tagLower.split('promo:')[1].replace('[comma]', ',')
            break
          }
        }

        return promoBadgeText
      },

      isExpandable () {
        return this.swatches.length > 7
      },
      link () {
        let collection = ''
        const pathname = window.location.pathname

        if (pathname.includes('collections')) {
          collection = pathname
        }

        return `${collection}/products/${this.handle}?variant=${this.activeVariant.id}`
      },
      colorKey () {
        return this.optionNames.find(option => option.toLowerCase() === 'color')
      },
      colorOptionIndex () {
        return this.optionNames.indexOf(this.colorKey)
      },
      swatches () {
        if (!this.colorKey) return false
        return this.optionsWithValues[this.colorKey]
      },
      shouldLazyLoadPrimaryImage () {
        return window.innerWidth > 768
      }
    },
    methods: {
      ...mapActions('global', [
        'openModal'
      ]),
      handleimageArrowClick (event, productId, link) {
        if (jQuery('.collection__grid').hasClass('collection__grid-mobile-one') || jQuery('.collection__grid').hasClass('collection__grid-mobile-two') || jQuery('.collection__grid').hasClass('collection__grid-mobile-four')) {
          if (jQuery('.card__featured-' + productId).hasClass('product_hide')) {
            jQuery('.product-card__featured').addClass('product_hide')
            jQuery('.card__featured-' + productId).removeClass('product_hide')
            event.preventDefault()
          } else {
            window.location.href = link
          }
        }
      },
      openQuickView () {
        this.openModal(`quickView${this.id}`)
      },
      filteredImages () {
        this.galleryimages = this.getfilteredImages()
        return this.gallery_images
      },
      removeSwatchImages (arr) {
        if (arr.length === 2) {
          if (arr[1].alt) {
            const sanitizeAlt = slug(arr[1].alt)

            if (sanitizeAlt.includes('swatch_')) return arr.slice(0, 1)
          }
        }

        return arr
      },
      checkProductBadges () {
        const badgesHandles = this.tags.map(x => slug(x))

        for (const key in window.productBadges) {
          if (badgesHandles.indexOf(key) > -1) {
            this.productBadges = productBadges[key]
            break
          }
        }
      },
      checkProductBadgesAllValue () {
        const badgesHandles = this.tags.map(x => slug(x))

        for (const key in window.productBadges) {
          if (badgesHandles.indexOf(key) > -1) {
            this.productBadgesValues += productBadges[key]
          }
        }
      },
      getfilteredImages (value = '') {
        let imagesArr = []

        if (this.swatches && this.activeVariant.sku) {
          const activeVariantSku = slug(this.activeVariant.sku)

          imagesArr = this.images.filter(img => {
            if (!img.alt) {
              return
            }

            let altText = img.alt
            img.class = ''
            const altTextlower = img.alt.toLowerCase()

            if (value) {
              const selectedcolor = 'swatch_' + value
              const selectedcolorlower = selectedcolor.toLowerCase()

              if (altTextlower === selectedcolorlower) {
                img.class = 'active'
              }
            } else {
              const firsttimeselectedcolor = 'swatch_' + this.selectedColor

              if (altTextlower === firsttimeselectedcolor) {
                img.class = 'active'
              }
            }

            if (img.alt.includes('|')) {
              altText = img.alt.split('|').map(x => slug(x))
            }

            return altText.indexOf(activeVariantSku) > -1
          })
        }

        if (imagesArr.length <= 0) {
          imagesArr = this.images
        }
        const slicedImagesArr = this.removeSwatchImages(imagesArr)
        this.totalImages = slicedImagesArr.length
        // if (slicedImagesArr.length > 0) {
        //   slicedImagesArr[0].class = 'active'
        // }
        return slicedImagesArr
      },
      expandMoreSwatches () {
        this.$refs.colorSwatches[0].classList.remove('is-expandable')
      },
      generateSrcset (url) {
        return generateSrcset(url)
      },
      slug (value) {
        return slug(value)
      },
      swatchImage (value) {
        const slugValue = slug(value)
        let backgroundImage = ''

        if (this.swatchImages) {
          const imageSrc = this.swatchImages.find(x => x.handle === slugValue)

          if (imageSrc) {
            backgroundImage = `background-image: url(${imageSrc.src.replace(/([.](?:jpe?g|png))/, '_80x$1')})`
          }
        }

        return backgroundImage
      },
      setColorOption (value) {
        this.galleryimages = this.getfilteredImages(value)
        this.selectedColor = slug(value)
        const colorOptionNumber = `option${this.colorOptionIndex + 1}`

        this.activeVariant = this.variants.find(variant => {
          return slug(variant[colorOptionNumber]) === this.selectedColor
        })
      },
      imageLoaded () {
        this.imagesLoaded++
        if (this.imagesLoaded === this.totalImages) {
          this.initializeFlickity()
        }
      },
      // Initialize Flickity on hover
      initializeFlickity () {
        this.enableImagecarouselOnHover = true
        const screenWidth = window.innerWidth
        // Check screen size and apply a class based on the condition
        if (screenWidth > 768) {
          this.hoverClass = 'large-screen'
          this.itemDesktop = document.querySelector('.collection__grid').getAttribute('data-desktop-grid')
        } else {
          this.hoverClass = 'small-screen'
          this.itemMobile = document.querySelector('.collection__grid').getAttribute('data-mobile-grid')
        }

        this.$nextTick(() => {
          if (this.delayedGalleryImages.length > 0 && !this.flkty) {
            this.flkty = new Flickity(this.$refs.carouselplp, {
              prevNextButtons: true,
              pageDots: false,
              cellAlign: 'left',
              wrapAround: true,
              lazyLoad: true,
              contain: true,
              imagesLoaded: true,
              groupCells: 1,
              freeScroll: false
            })
          }
        })

        this.setImageDimensionSlider()
      },
      // Destroy Flickity after hover ends
      destroyFlickity () {
        this.enableImagecarouselOnHover = false

        if (this.flkty) {
          this.flkty.destroy()
          this.flkty = null // Reset Flickity instance
        }
      },
      setImageDimensionSlider () {
        const parentSliderDivs = document.querySelectorAll('.carousel-plp-data')

        parentSliderDivs.forEach(function (parentSliderDiv) {
          if (parentSliderDiv) {
            const outerHeight = parentSliderDiv.clientHeight
            const outerWidth = parentSliderDiv.clientWidth

            const images = parentSliderDiv.querySelectorAll('.plp-shopby__list--item')
            images.forEach(function (img) {
              img.style.height = outerHeight + 'px'
              img.style.width = outerWidth + 'px'
            })
          }
        })
      }

    }
  }))
})
